import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { RestaurantProvider } from "@/contexts/RestaurantContext";
import { RestaurantAuthProvider } from "@/contexts/RestaurantAuthContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import CookieConsentBanner from "@/components/cookie/CookieConsentBanner";
import CookieSettingsDialog from "@/components/cookie/CookieSettingsDialog";
import Landing from "./pages/Landing";

const Login = lazy(() => import("./pages/Login"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RestaurantAuthGuard = lazy(() => import("./components/restaurant/RestaurantAuthGuard"));
const MenuEditor = lazy(() => import("./components/restaurant/MenuEditor"));
const RestaurantDashboard = lazy(() => import("./components/restaurant/RestaurantDashboard"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminRestaurants = lazy(() => import("./pages/admin/AdminRestaurants"));
const AdminRestaurantDetail = lazy(() => import("./pages/admin/AdminRestaurantDetail"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const Anvandarvillkor = lazy(() => import("./pages/legal/Anvandarvillkor"));
const Integritetspolicy = lazy(() => import("./pages/legal/Integritetspolicy"));
const Cookiepolicy = lazy(() => import("./pages/legal/Cookiepolicy"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <AdminAuthProvider>
        <RestaurantProvider>
          <RestaurantAuthProvider>
            <CookieConsentProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <Suspense fallback={null}>
                    <Routes>
                      <Route path="/" element={<Landing />} />
                      <Route path="/login" element={<Login />} />
                      {/* Legal pages */}
                      <Route path="/anvandarvillkor" element={<Anvandarvillkor />} />
                      <Route path="/integritetspolicy" element={<Integritetspolicy />} />
                      <Route path="/cookiepolicy" element={<Cookiepolicy />} />
                      {/* Restaurant user dashboard */}
                      <Route path="/dashboard" element={<RestaurantAuthGuard />}>
                        <Route index element={<RestaurantDashboard />} />
                        <Route path="edit" element={<MenuEditor />} />
                      </Route>
                      {/* Admin panel */}
                      <Route path="/admin/login" element={<AdminLogin />} />
                      <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="restaurants" element={<AdminRestaurants />} />
                        <Route path="restaurants/:id" element={<AdminRestaurantDetail />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="bookings" element={<AdminBookings />} />
                        <Route path="notifications" element={<AdminNotifications />} />
                        <Route path="settings" element={<AdminSettings />} />
                      </Route>
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
                <CookieConsentBanner />
                <CookieSettingsDialog />
              </TooltipProvider>
            </CookieConsentProvider>
          </RestaurantAuthProvider>
        </RestaurantProvider>
      </AdminAuthProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
